import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Target,
  Shield,
  Gamepad2,
  Activity,
  CheckSquare,
  FileText,
  Users,
  ArrowRight,
  ArrowDown,
  Network,
  Compass,
  Award,
  AlertTriangle,
  Calendar,
  TrendingUp,
  GitBranch,
  Play,
  ChevronRight,
  Check } from
'lucide-react';
import FileRepositoryCard from '@/components/overall_architecture/FileRepositoryCard';
import LivingSystem from '@/components/overall_architecture/LivingSystem';

export default function OverallArchitecture() {
  const [selectedBox, setSelectedBox] = useState(null);
  const [simulationMode, setSimulationMode] = useState(false);
  const [simulationStep, setSimulationStep] = useState(0);
  const [showFlow, setShowFlow] = useState(false);

  const simulationSteps = [
  {
    step: 0,
    title: "Welcome to COMPASS Simulation",
    description: "Experience the end-to-end flow of how COMPASS transforms strategic intent into daily execution. You'll play the role of Captain Nova, a mid-level officer.",
    highlight: null,
    content: null
  },
  {
    step: 1,
    title: "1. Strategic Framework - Direction",
    description: "Your organization has defined a Direction node: 'Become the market leader in AI-powered automation by 2027'",
    highlight: "framework",
    content: {
      type: "framework",
      data: {
        layer: "Direction",
        statement: "Become the market leader in AI-powered automation by 2027",
        owner: "CEO"
      }
    }
  },
  {
    step: 2,
    title: "2. Strategic Framework - Strategy → Tactical Priorities",
    description: "Strategic and tactical layers cascade from direction, defining how the organization will achieve its goals.",
    highlight: "framework",
    content: {
      type: "framework",
      data: {
        layer: "Strategy → Tactics",
        statement: "Strategy: Become the go-to solution for 'Information workers heavy' industries\n\nTactics: Self Service AI automation platform",
        owner: "Commander Caine"
      }
    }
  },
  {
    step: 3,
    title: "3. Mission Assigned - Campaign Mode",
    description: "A Campaign mission is spawned from this strategic and tactical priority. Commander Caine assigned you as the mission owner.",
    highlight: "missions",
    content: {
      type: "mission",
      data: {
        code: "C001",
        title: "Launch the AI Automation Platform for our largest client, MacroStuff LLC",
        mode: "Campaign",
        xp_reward: 500,
        assigned_to: "Captain Nova (You)"
      }
    }
  },
  {
    step: 4,
    title: "4. Objective Discovery - SMART Goals",
    description: "With the help of Compass, you iteratively discover an objective using the SMART framework to bridge the mission (Commander Caine's intent + his context briefing with what drives you (hand free design and removing slop).",
    highlight: "objectives",
    content: {
      type: "objective",
      data: {
        code: "C001-O1",
        statement: "Build a no touch, conversational AI with no hallucination by Q2 2026",
        bridge_score: 92,
        smart_score: 88,
        status: "Active"
      }
    }
  },
  {
    step: 5,
    title: "5. Activity Breakdown - RACI Assignment",
    description: "The objective spawns an activity focused on customer discovery. Since you deem this to be Urgent & Important, as per the Eisenhower matrix, you keep the Responsible role for this activity.",
    highlight: "activities",
    content: {
      type: "activity",
      data: {
        code: "C001-O1-A2",
        title: "Conduct Customer Discovery Interviews",
        responsible: "Captain Nova (You)",
        accountable: "Captain Nova (You)",
        key_results: ["Interview 20 target customers", "Document 50+ pain points"],
        skills_required: ["Research (Lvl 2)", "Communication (Lvl 3)"],
        eisenhower: "Urgent & Important"
      }
    }
  },
  {
    step: 6,
    title: "6. Daily Task Planning",
    description: "You break down the activity into today's prioritized task with time allocation.",
    highlight: "dailyTasks",
    content: {
      type: "task",
      data: {
        date: "Today",
        task_1: "Prepare interview questions and schedule 5 customer calls",
        time_allocation: "60%",
        key_result: "Interview 20 target customers",
        status: "Imminently Conquering"
      }
    }
  },
  {
    step: 7,
    title: "7. Submit Progress Report",
    description: "You report progress against key results and activity status back to your accountable officer (yourself in this case). You also identify issues encountered during execution.",
    highlight: "activities",
    content: {
      type: "progress_report",
      data: {
        activity: "Conduct Customer Discovery Interviews",
        reported_by: "Captain Nova (You)",
        key_results_progress: [
        { description: "Interview 20 target customers", current: "12 completed", target: "20" },
        { description: "Document 50+ pain points", current: "38 documented", target: "50+" }],

        status: "In Progress - On Track",
        issues_encountered: [
        {
          description: "Customers struggle with API complexity",
          severity: "Medium",
          recommendation: "Need simplified API documentation and developer tools"
        }]

      }
    }
  },
  {
    step: 8,
    title: "8. Principles Adherence Reporting",
    description: "As part of your progress report, you self-report how you upheld organizational values and navigated red lines.",
    highlight: "principles",
    content: {
      type: "principles",
      data: {
        values_adhered: ["Customer Obsession", "Transparency"],
        red_lines: [
        { line: "Never misrepresent capabilities to customers", status: "Upheld" },
        { line: "Respect customer time and boundaries", status: "Upheld" }],

        narrative: "Maintained full transparency about MVP limitations while deeply listening to customer needs."
      }
    }
  },
  {
    step: 9,
    title: "9. Commander Validation",
    description: "As the accountable officer (yourself), you validate both the performance against key results and principles adherence, awarding bonuses for strong execution.",
    highlight: "validation",
    content: {
      type: "validation",
      data: {
        status: "Validated",
        xp_bonus: 50,
        sp_bonus: 25,
        notes: "Excellent demonstration of customer-first principles. Leadership in action.",
        new_xp: 2500,
        new_leadership_sp: 175
      }
    }
  },
  {
    step: 10,
    title: "10. Feedback Loop Closes",
    description: "Insights from your interviews reveal a critical capability gap. An issue is tagged, policy designed, and feedback flows back up the framework.",
    highlight: "feedback",
    content: {
      type: "feedback",
      data: {
        issue: "Customers need real-time API access, but we lack infrastructure",
        decision: "Design interim policy + Request capability development",
        policy: "Manual API key provisioning until automation ready",
        capability: "Automated API Gateway System",
        impact: "Execution insights → Operations adjustment → Tactical pivot"
      }
    }
  },
  {
    step: 11,
    title: "Simulation Complete! 🎉",
    description: "You've experienced the full COMPASS flow: Strategic intent cascaded down to daily execution, principles validated, and feedback looped back up. This is how purpose transforms into execution.",
    highlight: null,
    content: {
      type: "complete",
      data: {
        summary: [
        "Strategic Direction → Campaign Mission",
        "SMART Objective → RACI Activity",
        "Daily Task Execution → Principles Adherence",
        "Commander Validation → XP/SP Rewards",
        "Issues Identified → Feedback Loop Closed"]

      }
    }
  }];


  const currentStep = simulationSteps[simulationStep];

  const nextStep = () => {
    if (simulationStep < simulationSteps.length - 1) {
      setSimulationStep(simulationStep + 1);
    }
  };

  const prevStep = () => {
    if (simulationStep > 0) {
      setSimulationStep(simulationStep - 1);
    }
  };

  const startSimulation = () => {
    setSimulationMode(true);
    setSimulationStep(0);
  };

  const exitSimulation = () => {
    setSimulationMode(false);
    setSimulationStep(0);
    setSelectedBox(null);
  };

  const architectureBoxes = {
    framework: {
      icon: Target,
      title: "Strategic Framework",
      color: "from-indigo-500 to-indigo-700",
      description: "Purpose → Direction → Strategy → Tactics → Operations → Execution hierarchy. Right-to-left feedback flows back up.",
      position: "top-left"
    },
    codex: {
      icon: Shield,
      title: "The Codex",
      color: "from-rose-500 to-rose-700",
      description: "Noble Why, Core Values, Operating Principles, and Red Lines. The immutable foundation guiding all decisions.",
      position: "top-center"
    },
    officers: {
      icon: Users,
      title: "Officer Profiles",
      color: "from-cyan-500 to-cyan-700",
      description: "Callsigns, ranks, clearance levels, skills (XP/SP), badges. Officers progress through gamified leveling.",
      position: "top-right"
    },
    missions: {
      icon: Gamepad2,
      title: "Missions",
      color: "from-purple-500 to-purple-700",
      description: "Rank & File, Campaigns, Special Ops. Each mission spawns objectives with strategic intent.",
      position: "mid-left"
    },
    objectives: {
      icon: Compass,
      title: "Objectives",
      color: "from-emerald-500 to-emerald-700",
      description: "SMART objectives discovered iteratively. Bridge quality scores ensure alignment with framework.",
      position: "mid-center"
    },
    activities: {
      icon: Activity,
      title: "Activities",
      color: "from-amber-500 to-amber-700",
      description: "RACI-based execution units with key results, skill requirements/rewards, and Eisenhower prioritization.",
      position: "mid-right"
    },
    dailyTasks: {
      icon: CheckSquare,
      title: "Daily Tasks",
      color: "from-blue-500 to-blue-700",
      description: "3 prioritized tasks per day per officer. Time allocation, status flags, linked to activities.",
      position: "bottom-left"
    },
    principles: {
      icon: Award,
      title: "Principles Adherence",
      color: "from-indigo-500 to-purple-700",
      description: "Officers self-report values/principles adherence and red line considerations across activities and tasks.",
      position: "bottom-center"
    },
    validation: {
      icon: TrendingUp,
      title: "Commander Validation",
      color: "from-pink-500 to-pink-700",
      description: "Commanders review and award XP/SP bonuses or maluses based on principles adherence.",
      position: "bottom-right"
    },
    artifacts: {
      icon: FileText,
      title: "Artifacts & Rituals",
      color: "from-teal-500 to-teal-700",
      description: "Funnels, dashboards, SOPs, templates. Rituals define recurring meetings. Both have approval workflows.",
      position: "side-top"
    },
    issues: {
      icon: AlertTriangle,
      title: "Issues & Policies",
      color: "from-orange-500 to-orange-700",
      description: "Tagged issues from execution → design policies → request capabilities. Close the feedback loop.",
      position: "side-middle"
    },
    feedback: {
      icon: GitBranch,
      title: "Feedback Loops",
      color: "from-slate-500 to-slate-700",
      description: "Right-to-left: Execution insights flow back to Operations, Tactics, Strategy. Real-time organizational learning.",
      position: "side-bottom"
    }
  };

  if (showFlow) {
    return <LivingSystem onBack={() => setShowFlow(false)} />;
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Overall Architecture</h1>
          <p className="text-slate-400">Interactive map of how COMPASS connects purpose to execution</p>
        </div>
        {!simulationMode &&
        <div className="flex items-center gap-3">
          <Button
            onClick={startSimulation}
            className="bg-gradient-to-r from-cyan-600 to-indigo-600 hover:from-cyan-700 hover:to-indigo-700">
            <Play className="w-4 h-4 mr-2" />
            Start Interactive Simulation
          </Button>
          <Button
            onClick={() => setShowFlow(true)}
            className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700">
            <Play className="w-4 h-4 mr-2" />
            Start Flow
          </Button>
        </div>
        }
      </div>

      {/* Simulation Overlay */}
      <AnimatePresence>
        {simulationMode &&
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-slate-950/95 backdrop-blur-sm z-50 flex items-center justify-center p-8">

            <Card className="glass-panel border-none max-w-4xl w-full">
              <CardContent className="p-8">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                      {simulationStep}
                    </div>
                    <div>
                      <h2 className="text-slate-600 text-2xl font-bold">{currentStep.title}</h2>
                      <p className="text-slate-400 mt-1">{currentStep.description}</p>
                    </div>
                  </div>
                  <Button data-testid="arch-exit-btn" variant="ghost" onClick={exitSimulation} className="text-slate-400">
                    Exit
                  </Button>
                </div>

                {/* Step Content */}
                {currentStep.content &&
              <motion.div
                key={simulationStep}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6">

                    {currentStep.content.type === "framework" &&
                <div className="bg-indigo-400 p-6 rounded-lg from-indigo-500/20 to-indigo-700/20 border border-indigo-500/30">
                        <Badge className="mb-3 bg-indigo-500 text-white">{currentStep.content.data.layer}</Badge>
                        <p className="text-xl text-slate-100 font-semibold mb-2 whitespace-pre-line">{currentStep.content.data.statement}</p>
                        <p className="text-slate-50 text-sm">Owner: {currentStep.content.data.owner}</p>
                      </div>
                }

                    {currentStep.content.type === "mission" &&
                <div className="bg-violet-200 p-6 rounded-lg from-purple-500/20 to-purple-700/20 border border-purple-500/30">
                        <div className="flex items-center gap-2 mb-3">
                          <Badge className="bg-purple-500 text-white">{currentStep.content.data.mode}</Badge>
                          <Badge variant="outline" className="text-purple-600 px-2.5 py-0.5 text-xs font-semibold rounded-md inline-flex items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-purple-400">{currentStep.content.data.code}</Badge>
                        </div>
                        <p className="text-slate-600 mb-2 text-xl font-semibold">{currentStep.content.data.title}</p>
                        <div className="flex items-center gap-4 mt-3">
                          <p className="text-slate-600 text-sm">Assigned to: {currentStep.content.data.assigned_to}</p>
                          <Badge className="bg-amber-500/20 text-amber-600 px-2.5 py-0.5 text-xs font-semibold rounded-md inline-flex items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent shadow hover:bg-primary/80">+{currentStep.content.data.xp_reward} XP</Badge>
                        </div>
                      </div>
                }

                    {currentStep.content.type === "objective" &&
                <div className="bg-slate-100 p-6 rounded-lg from-emerald-500/20 to-emerald-700/20 border border-emerald-500/30">
                        <Badge className="mb-3 bg-emerald-500 text-white">{currentStep.content.data.code}</Badge>
                        <p className="text-slate-600 mb-4 text-xl font-semibold">{currentStep.content.data.statement}</p>
                        <div className="flex gap-4">
                          <Badge variant="outline" className="text-indigo-300 border-indigo-400">
                            Bridge Score: {currentStep.content.data.bridge_score}%
                          </Badge>
                          <Badge variant="outline" className="text-cyan-300 border-cyan-400">
                            SMART Score: {currentStep.content.data.smart_score}%
                          </Badge>
                          <Badge className="bg-emerald-500/20 text-emerald-400">{currentStep.content.data.status}</Badge>
                        </div>
                      </div>
                }

                    {currentStep.content.type === "activity" &&
                <div className="p-6 bg-gradient-to-br from-amber-500/20 to-amber-700/20 rounded-lg border border-amber-500/30">
                        <Badge className="mb-3 bg-amber-500 text-white">{currentStep.content.data.code}</Badge>
                        <p className="text-slate-600 mb-4 text-xl font-semibold">{currentStep.content.data.title}</p>
                        <div className="space-y-2 text-sm">
                          <p className="text-slate-600"><strong className="text-amber-400">Responsible:</strong> {currentStep.content.data.responsible}</p>
                          <p className="text-slate-600"><strong className="text-amber-400">Accountable:</strong> {currentStep.content.data.accountable}</p>
                          <p className="text-slate-600"><strong className="text-amber-400">Key Results:</strong> {currentStep.content.data.key_results.join(", ")}</p>
                          <p className="text-slate-600"><strong className="text-amber-400">Skills Required:</strong> {currentStep.content.data.skills_required.join(", ")}</p>
                          <Badge className="mt-2 bg-rose-500/20 text-rose-400">{currentStep.content.data.eisenhower}</Badge>
                        </div>
                      </div>
                }

                    {currentStep.content.type === "task" &&
                <div className="p-6 bg-gradient-to-br from-blue-500/20 to-blue-700/20 rounded-lg border border-blue-500/30">
                        <div className="flex items-center gap-2 mb-3">
                          <Badge className="bg-blue-500 text-white">Task #1</Badge>
                          <Badge variant="outline" className="text-blue-600 px-2.5 py-0.5 text-xs font-semibold rounded-md inline-flex items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-blue-400">{currentStep.content.data.date}</Badge>
                        </div>
                        <p className="text-slate-600 mb-4 text-xl font-semibold">{currentStep.content.data.task_1}</p>
                        <div className="space-y-2 text-sm">
                          <p className="text-slate-600"><strong className="text-blue-400">Time Allocation:</strong> {currentStep.content.data.time_allocation}</p>
                          <p className="text-slate-600"><strong className="text-blue-400">Advances:</strong> {currentStep.content.data.key_result}</p>
                          <Badge className="bg-yellow-500/20 text-yellow-600 mt-2 px-2.5 py-0.5 text-xs font-semibold rounded-md inline-flex items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent shadow hover:bg-primary/80">{currentStep.content.data.status}</Badge>
                        </div>
                      </div>
                }

                    {currentStep.content.type === "progress_report" &&
                <div className="p-6 bg-gradient-to-br from-amber-500/20 to-amber-700/20 rounded-lg border border-amber-500/30">
                        <div className="text-slate-600 mb-4 flex items-center gap-2">
                          <Activity className="w-5 h-5 text-amber-400" />
                          <h4 className="text-slate-600 font-semibold">Progress Report</h4>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <p className="text-slate-600 mb-2 text-sm">Activity:</p>
                            <p className="text-slate-600 font-semibold">{currentStep.content.data.activity}</p>
                            <p className="text-slate-600 mt-1 text-xs">Reported by: {currentStep.content.data.reported_by}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-400 mb-2">Key Results Progress:</p>
                            <div className="space-y-2">
                              {currentStep.content.data.key_results_progress.map((kr, i) =>
                        <div key={i} className="bg-slate-50 p-3 rounded border border-amber-500/20">
                                  <p className="text-slate-600 mb-1 text-sm">{kr.description}</p>
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-amber-300 border-amber-400 text-xs">
                                      {kr.current} / {kr.target}
                                    </Badge>
                                  </div>
                                </div>
                        )}
                            </div>
                          </div>
                          <div>
                            <p className="text-sm text-slate-400 mb-1">Status:</p>
                            <Badge className="bg-emerald-500/20 text-emerald-400">{currentStep.content.data.status}</Badge>
                          </div>
                          <div className="pt-3 border-t border-slate-700">
                            <p className="text-sm text-slate-400 mb-2">Issues Encountered:</p>
                            {currentStep.content.data.issues_encountered.map((issue, i) =>
                      <div key={i} className="p-3 bg-rose-500/10 rounded border border-rose-500/30 mb-2">
                                <div className="flex items-center gap-2 mb-1">
                                  <AlertTriangle className="w-4 h-4 text-rose-400" />
                                  <p className="text-slate-600 text-sm font-semibold">{issue.description}</p>
                                </div>
                                <p className="text-slate-600 text-xs">Severity: {issue.severity}</p>
                                <p className="text-slate-600 mt-1 text-xs">→ {issue.recommendation}</p>
                              </div>
                      )}
                          </div>
                        </div>
                      </div>
                }

                    {currentStep.content.type === "principles" &&
                <div className="p-6 bg-gradient-to-br from-indigo-500/20 to-purple-700/20 rounded-lg border border-indigo-500/30">
                        <h4 className="text-slate-600 mb-3 font-semibold flex items-center gap-2">
                          <Shield className="w-5 h-5 text-indigo-400" />
                          Self-Reported Principles Adherence
                        </h4>
                        <div className="space-y-4">
                          <div>
                            <p className="text-slate-700 mb-2 text-sm">Values Upheld:</p>
                            <div className="flex flex-wrap gap-2">
                              {currentStep.content.data.values_adhered.map((v, i) =>
                        <Badge key={i} className="bg-indigo-500/20 text-indigo-600 px-2.5 py-0.5 text-xs font-semibold rounded-md inline-flex items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 shadow hover:bg-primary/80 border-indigo-500/30">
                                  {v}
                                </Badge>
                        )}
                            </div>
                          </div>
                          <div>
                            <p className="text-red-600 mb-2 text-sm">Red Lines:</p>
                            {currentStep.content.data.red_lines.map((rl, i) =>
                      <div key={i} className="flex items-center gap-2 mb-1">
                                <Check className="w-4 h-4 text-emerald-400" />
                                <p className="text-slate-600 text-sm">{rl.line} - <span className="text-emerald-400">{rl.status}</span></p>
                              </div>
                      )}
                          </div>
                          <div className="pt-3 border-t border-slate-700">
                            <p className="text-slate-700 mb-1 text-sm">Narrative:</p>
                            <p className="text-slate-600 italic">"{currentStep.content.data.narrative}"</p>
                          </div>
                        </div>
                      </div>
                }

                    {currentStep.content.type === "validation" &&
                <div className="p-6 bg-gradient-to-br from-pink-500/20 to-pink-700/20 rounded-lg border border-pink-500/30">
                        <h4 className="text-slate-600 mb-4 font-semibold flex items-center gap-2">
                          <TrendingUp className="w-5 h-5 text-pink-400" />
                          Commander's Validation
                        </h4>
                        <div className="space-y-4">
                          <div className="bg-slate-50 p-4 rounded-lg border border-emerald-500/30">
                            <p className="text-slate-600 mb-2 text-xs">Performance Validation</p>
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className="bg-emerald-500/20 text-emerald-400">
                                <Check className="w-3 h-3 mr-1" />
                                Key Results: On Track
                              </Badge>
                            </div>
                            <p className="text-slate-500 text-sm">Strong progress on customer interviews and pain point documentation.</p>
                          </div>
                          <div className="bg-slate-50 p-4 rounded-lg border border-indigo-500/30">
                            <p className="text-slate-600 mb-2 text-xs">Principles Validation</p>
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className="bg-emerald-500 text-white">
                                <Check className="w-3 h-3 mr-1" />
                                {currentStep.content.data.status}
                              </Badge>
                            </div>
                            <p className="text-slate-500 text-sm italic">"{currentStep.content.data.notes}"</p>
                          </div>
                          <div className="flex gap-4 pt-3">
                            <Badge className="bg-amber-500/20 text-amber-600 px-4 py-2 text-base font-semibold rounded-md inline-flex items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent shadow hover:bg-primary/80">
                              +{currentStep.content.data.xp_bonus} XP Bonus
                            </Badge>
                            <Badge className="bg-cyan-500/20 text-cyan-600 px-4 py-2 text-base font-semibold rounded-md inline-flex items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent shadow hover:bg-primary/80">
                              +{currentStep.content.data.sp_bonus} Leadership SP
                            </Badge>
                          </div>
                          <div className="pt-3 border-t border-slate-700 flex gap-6">
                            <div>
                              <p className="text-xs text-slate-500">New Total XP</p>
                              <p className="text-xl font-bold text-amber-400">{currentStep.content.data.new_xp}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Leadership Skill XP</p>
                              <p className="text-xl font-bold text-cyan-400">{currentStep.content.data.new_leadership_sp}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                }

                    {currentStep.content.type === "feedback" &&
                <div className="p-6 bg-gradient-to-br from-slate-500/20 to-slate-700/20 rounded-lg border border-slate-500/30">
                        <div className="space-y-4">
                          <div>
                            <p className="text-red-600 mb-1 text-sm">Issue Identified:</p>
                            <p className="text-slate-600 font-semibold">{currentStep.content.data.issue}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <ArrowRight className="w-5 h-5 text-slate-500" />
                            <p className="text-slate-700 text-sm"><strong className="text-orange-400">Decision:</strong> {currentStep.content.data.decision}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="p-3 bg-orange-500/10 rounded border border-orange-500/30">
                              <p className="text-slate-600 mb-1 text-xs font-bold">Interim Policy</p>
                              <p className="text-slate-600 text-sm">{currentStep.content.data.policy}</p>
                            </div>
                            <div className="p-3 bg-teal-500/10 rounded border border-teal-500/30">
                              <p className="text-slate-600 mb-1 text-xs font-bold">Capability Requested</p>
                              <p className="text-slate-600 text-sm">{currentStep.content.data.capability}</p>
                            </div>
                          </div>
                          <div className="pt-3 border-t border-slate-700">
                            <p className="text-sm text-emerald-400 font-semibold">{currentStep.content.data.impact}</p>
                          </div>
                        </div>
                      </div>
                }

                    {currentStep.content.type === "complete" &&
                <div className="p-6 bg-gradient-to-br from-emerald-500/20 to-cyan-700/20 rounded-lg border border-emerald-500/30">
                        <h4 className="text-slate-100 font-semibold mb-4 flex items-center gap-2">
                          <Award className="w-5 h-5 text-emerald-400" />
                          Journey Summary
                        </h4>
                        <div className="space-y-2">
                          {currentStep.content.data.summary.map((item, i) =>
                    <div key={i} className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-emerald-400" />
                              <p className="text-slate-300">{item}</p>
                            </div>
                    )}
                        </div>
                      </div>
                }
                  </motion.div>
              }

                {/* Navigation */}
                <div className="flex items-center justify-between pt-6 border-t border-slate-800">
                  <Button
                  variant="outline"
                  onClick={prevStep}
                  disabled={simulationStep === 0}
                  className="border-slate-700">

                    Previous
                  </Button>
                  
                  <div className="flex gap-2">
                    {simulationSteps.map((_, idx) =>
                  <div
                    key={idx}
                    className={`w-2 h-2 rounded-full transition-all ${
                    idx === simulationStep ?
                    'bg-cyan-400 w-6' :
                    idx < simulationStep ?
                    'bg-emerald-400' :
                    'bg-slate-700'}`
                    } />

                  )}
                  </div>

                  {simulationStep < simulationSteps.length - 1 ?
                <Button
                  onClick={nextStep}
                  className="bg-gradient-to-r from-cyan-600 to-indigo-600 hover:from-cyan-700 hover:to-indigo-700">

                      Next
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button> :

                <Button
                  onClick={exitSimulation}
                  className="bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700">

                      <Check className="w-4 h-4 mr-2" />
                      Complete
                    </Button>
                }
                </div>
              </CardContent>
            </Card>
          </motion.div>
        }
      </AnimatePresence>

      {/* Main Architecture Flow */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Row: Foundation Layer */}
        <ArchitectureBox
          {...architectureBoxes.framework}
          isSelected={selectedBox === 'framework'}
          onClick={() => setSelectedBox(selectedBox === 'framework' ? null : 'framework')}
          isHighlighted={simulationMode && currentStep.highlight === 'framework'} />

        <ArchitectureBox
          {...architectureBoxes.codex}
          isSelected={selectedBox === 'codex'}
          onClick={() => setSelectedBox(selectedBox === 'codex' ? null : 'codex')}
          isHighlighted={simulationMode && currentStep.highlight === 'codex'} />

        <ArchitectureBox
          {...architectureBoxes.officers}
          isSelected={selectedBox === 'officers'}
          onClick={() => setSelectedBox(selectedBox === 'officers' ? null : 'officers')}
          isHighlighted={simulationMode && currentStep.highlight === 'officers'} />


        {/* Flow Arrow */}
        <div className="lg:col-span-3 flex justify-center">
          <ArrowDown className="w-8 h-8 text-slate-600" />
        </div>

        {/* Middle Row: Execution Layer */}
        <ArchitectureBox
          {...architectureBoxes.missions}
          isSelected={selectedBox === 'missions'}
          onClick={() => setSelectedBox(selectedBox === 'missions' ? null : 'missions')}
          isHighlighted={simulationMode && currentStep.highlight === 'missions'} />

        <ArchitectureBox
          {...architectureBoxes.objectives}
          isSelected={selectedBox === 'objectives'}
          onClick={() => setSelectedBox(selectedBox === 'objectives' ? null : 'objectives')}
          isHighlighted={simulationMode && currentStep.highlight === 'objectives'} />

        <ArchitectureBox
          {...architectureBoxes.activities}
          isSelected={selectedBox === 'activities'}
          onClick={() => setSelectedBox(selectedBox === 'activities' ? null : 'activities')}
          isHighlighted={simulationMode && currentStep.highlight === 'activities'} />


        {/* Flow Arrow */}
        <div className="lg:col-span-3 flex justify-center">
          <ArrowDown className="w-8 h-8 text-slate-600" />
        </div>

        {/* Bottom Row: Daily Execution & Validation */}
        <ArchitectureBox
          {...architectureBoxes.dailyTasks}
          isSelected={selectedBox === 'dailyTasks'}
          onClick={() => setSelectedBox(selectedBox === 'dailyTasks' ? null : 'dailyTasks')}
          isHighlighted={simulationMode && currentStep.highlight === 'dailyTasks'} />

        <ArchitectureBox
          {...architectureBoxes.principles}
          isSelected={selectedBox === 'principles'}
          onClick={() => setSelectedBox(selectedBox === 'principles' ? null : 'principles')}
          isHighlighted={simulationMode && currentStep.highlight === 'principles'} />

        <ArchitectureBox
          {...architectureBoxes.validation}
          isSelected={selectedBox === 'validation'}
          onClick={() => setSelectedBox(selectedBox === 'validation' ? null : 'validation')}
          isHighlighted={simulationMode && currentStep.highlight === 'validation'} />

      </div>

      {/* Side Systems */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
        <ArchitectureBox
          {...architectureBoxes.artifacts}
          isSelected={selectedBox === 'artifacts'}
          onClick={() => setSelectedBox(selectedBox === 'artifacts' ? null : 'artifacts')}
          isHighlighted={simulationMode && currentStep.highlight === 'artifacts'} />

        <ArchitectureBox
          {...architectureBoxes.issues}
          isSelected={selectedBox === 'issues'}
          onClick={() => setSelectedBox(selectedBox === 'issues' ? null : 'issues')}
          isHighlighted={simulationMode && currentStep.highlight === 'issues'} />

        <ArchitectureBox
          {...architectureBoxes.feedback}
          isSelected={selectedBox === 'feedback'}
          onClick={() => setSelectedBox(selectedBox === 'feedback' ? null : 'feedback')}
          isHighlighted={simulationMode && currentStep.highlight === 'feedback'} />

      </div>

      {/* Integration Flow Diagram */}
      <Card className="glass-panel border-none mt-12">
        <CardContent className="p-8">
          <h2 className="text-slate-600 mb-6 text-2xl font-bold flex items-center gap-2">Data Flow & Integration


          </h2>
          
          <div className="space-y-6">
            <FlowStep
              number="1"
              title="Strategic Alignment"
              description="Framework nodes define strategic direction → Missions inherit strategic intent"
              color="text-indigo-400" />

            <FlowStep
              number="2"
              title="Objective Discovery"
              description="Objectives bridge mission goals with SMART criteria → Commander approval for activation"
              color="text-emerald-400" />

            <FlowStep
              number="3"
              title="Activity Breakdown"
              description="Activities spawn from objectives with RACI, key results, and skill requirements"
              color="text-amber-400" />

            <FlowStep
              number="4"
              title="Daily Execution"
              description="Officers plan 3 daily tasks linked to activities with time allocation and principles reporting"
              color="text-blue-400" />

            <FlowStep
              number="5"
              title="Principles Validation"
              description="Commanders review adherence across activities, reports, and tasks → Award XP/SP bonuses or maluses"
              color="text-pink-400" />

            <FlowStep
              number="6"
              title="Feedback Loop"
              description="Issues tagged in execution → Policies designed or capabilities requested → Learning flows back up the framework"
              color="text-orange-400" />

          </div>
        </CardContent>
      </Card>

      {/* Key Concepts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="glass-panel border-none border-l-4 border-l-cyan-500">
          <CardContent className="p-6">
            <h3 className="text-slate-600 mb-3 text-lg font-semibold flex items-center gap-2">Gamification System


            </h3>
            <div className="space-y-2 text-sm text-slate-300">
              <p className="text-slate-500">• XP (Experience Points): General progression for completing activities and tasks</p>
              <p className="text-slate-500">• SP (Skill Points): Skill-specific progression awarded upon activity completion</p>
              <p>• <strong>Levels & Ranks:</strong> Officers level up (1-20) and advance ranks based on total XP</p>
              <p>• <strong>Clearance Levels:</strong> Unlock access to different types of missions and capabilities</p>
              <p className="text-slate-500">• Badges: Achievement system for special accomplishments</p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-panel border-none border-l-4 border-l-rose-500">
          <CardContent className="p-6">
            <h3 className="text-slate-600 mb-3 text-lg font-semibold flex items-center gap-2">Principles & Red Lines


            </h3>
            <div className="space-y-2 text-sm text-slate-300">
              <p>• <strong>Values & Principles:</strong> Officers self-report which organizational values they upheld</p>
              <p>• <strong>Red Lines:</strong> Critical boundaries with 3 states: upheld, navigated, or violated</p>
              <p>• <strong>Narratives:</strong> Free-text explanations of how principles were applied</p>
              <p>• <strong>Commander Validation:</strong> Review and award bonuses/maluses for principles adherence</p>
              <p>• <strong>Leadership Skill:</strong> Principles validation affects Leadership SP progression</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* File Repository */}
      <FileRepositoryCard />

      {/* Application Architecture Index */}
      <Card className="glass-panel border-none mt-12">
        <CardContent className="p-8">
          <h2 className="text-slate-600 mb-6 text-2xl font-bold flex items-center gap-2">
            Application Architecture Map
          </h2>
          
          <div className="space-y-8">
            {/* Core Pages */}
            <div>
              <h3 className="text-lg font-semibold text-indigo-400 mb-4">Core Pages</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <ArchItem name="Dashboard" type="Page" desc="Command deck with action items, metrics, and team overview" />
                <ArchItem name="Missions" type="Page" desc="Mission control center by game mode" />
                <ArchItem name="Discovery" type="Page" desc="AI-assisted objective discovery with SMART framework" />
                <ArchItem name="Activities" type="Page" desc="RACI activities with key results and progress reports" />
                <ArchItem name="DailyTasks" type="Page" desc="Daily task planner with squad view" />
                <ArchItem name="Artifacts" type="Page" desc="Artifacts & rituals library with approval workflows" />
                <ArchItem name="Issues" type="Page" desc="Issues, policies & capabilities management" />
                <ArchItem name="Framework" type="Page" desc="Strategic framework (Purpose → Execution)" />
                <ArchItem name="Codex" type="Page" desc="Immutable values, principles, and red lines" />
                <ArchItem name="OrgChart" type="Page" desc="Engines, officers, and mission assignments" />
                <ArchItem name="Profile" type="Page" desc="Officer profile with skills, drive, and user manual" />
                <ArchItem name="DocumentLibrary" type="Page" desc="Google Drive document management" />
                <ArchItem name="CivilizationMap" type="Page" desc="Interactive hex map visualization" />
                <ArchItem name="LighthouseViewer" type="Page" desc="External Lighthouse iframe" />
                <ArchItem name="Feedback" type="Page" desc="Bug reports and feature requests (admin only)" />
                <ArchItem name="OverallArchitecture" type="Page" desc="This page - system architecture overview" />
              </div>
            </div>

            {/* Mission & Strategy Components */}
            <div>
              <h3 className="text-lg font-semibold text-amber-400 mb-4">Mission & Strategy Components</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <ArchItem name="MissionClosureDialog" type="Dialog" desc="Complete missions with outcome, evidence, learnings" />
              </div>
            </div>

            {/* Objective Discovery Components */}
            <div>
              <h3 className="text-lg font-semibold text-emerald-400 mb-4">Objective Discovery Components</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <ArchItem name="ObjectiveCard" type="Card" desc="Objective display with SMART details and status" />
                <ArchItem name="ObjectiveSearch" type="Filter" desc="Search and filter objectives" />
                <ArchItem name="AIAssistedObjectiveDialog" type="Dialog" desc="AI-powered SMART objective discovery" />
              </div>
            </div>

            {/* Activity Components */}
            <div>
              <h3 className="text-lg font-semibold text-blue-400 mb-4">Activity Components</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <ArchItem name="GanttChart" type="View" desc="Table view of activities with drag-and-drop" />
                <ArchItem name="SkillRequirementsManager" type="Form" desc="Manage skill requirements and rewards" />
                <ArchItem name="PrinciplesSection" type="Form" desc="Principles adherence and red line reporting" />
                <ArchItem name="BotTaskGenerationSection" type="Card" desc="AI officer task generation interface" />
                <ArchItem name="AITaskApprovalSection" type="Card" desc="Approve AI-generated tasks" />
                <ArchItem name="TagHistoryDialog" type="Dialog" desc="View tag history across reports" />
                <ArchItem name="TagRemovalDialog" type="Dialog" desc="Remove tags from multiple reports" />
                <ArchItem name="RemovedIssuesSection" type="Section" desc="Display removed issues from reports" />
              </div>
            </div>

            {/* Daily Task Components */}
            <div>
              <h3 className="text-lg font-semibold text-cyan-400 mb-4">Daily Task Components</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <ArchItem name="DailyTaskPlanner" type="Complex" desc="Main daily task interface with squad, filters, gamification" />
                <ArchItem name="TaskCategorizationPanel" type="Form" desc="AI-powered task categorization (XP/EP/SP)" />
                <ArchItem name="MicroTaskListDialog" type="Dialog" desc="Quick to-do list per officer" />
                <ArchItem name="PendingBotTasksApproval" type="Card" desc="Approve AI-generated pending tasks" />
              </div>
            </div>

            {/* Dashboard Components */}
            <div>
              <h3 className="text-lg font-semibold text-purple-400 mb-4">Dashboard Components</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <ArchItem name="MetricCard" type="Card" desc="Visual metric display with progress bars" />
                <ArchItem name="ActionItemsDialog" type="Dialog" desc="Detailed action items by officer" />
                <ArchItem name="OpenIssuesDialog" type="Dialog" desc="Issues breakdown by officer" />
                <ArchItem name="StrugglingPrinciplesDialog" type="Dialog" desc="Principles adherence analysis" />
                <ArchItem name="StrategyTacticsDialog" type="Dialog" desc="Strategy/tactics breakdown with XP" />
                <ArchItem name="IssueTagsCard" type="Card" desc="Issue tags visualization" />
                <ArchItem name="PendingBotApprovalsCard" type="Card" desc="Bot approval queue for commanders" />
                <ArchItem name="VisualTeamMap" type="Card" desc="Officer network visualization" />
                <ArchItem name="ActionItemButtons" type="Utility" desc="Dismiss/snooze action item buttons" />
                <ArchItem name="TagDetailsDialog" type="Dialog" desc="Detailed tag information" />
                <ArchItem name="TaggedItemsDialog" type="Dialog" desc="Items tagged across system" />
                <ArchItem name="OpenIssuesTagTable" type="Table" desc="Issues grouped by tag" />
                <ArchItem name="TaggedActivityFilter" type="Filter" desc="Filter activities by tags" />
                <ArchItem name="StrugglingItemActivitiesDialog" type="Dialog" desc="Activities struggling with principles" />
                <ArchItem name="UserManualTable" type="Table" desc="Officer user manuals overview" />
              </div>
            </div>

            {/* Issue, Policy, Capability Components */}
            <div>
              <h3 className="text-lg font-semibold text-rose-400 mb-4">Issues, Policies & Capabilities</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <ArchItem name="IssueDialog" type="Dialog" desc="Create/edit issues with tagging" />
                <ArchItem name="IssueCard" type="Card" desc="Issue display with decision tracking" />
                <ArchItem name="IssueSearch" type="Filter" desc="Search and filter issues" />
                <ArchItem name="IssueScanDialog" type="Dialog" desc="Scan activities for issues" />
                <ArchItem name="PolicyDialog" type="Dialog" desc="Design policies" />
                <ArchItem name="PolicyCard" type="Card" desc="Policy display with effectiveness tracking" />
                <ArchItem name="ExistingPolicyDialog" type="Dialog" desc="Import existing policy" />
                <ArchItem name="CapabilityDialog" type="Dialog" desc="Request capability development" />
                <ArchItem name="CapabilityCard" type="Card" desc="Capability tracking" />
                <ArchItem name="ExistingCapabilityDialog" type="Dialog" desc="Import existing capability" />
              </div>
            </div>

            {/* Artifact & Ritual Components */}
            <div>
              <h3 className="text-lg font-semibold text-teal-400 mb-4">Artifacts & Rituals</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <ArchItem name="ArtifactSearch" type="Filter" desc="Search and filter artifacts" />
                <ArchItem name="RitualSearch" type="Filter" desc="Search and filter rituals" />
                <ArchItem name="ArtifactVersionHistory" type="Dialog" desc="Version history viewer" />
                <ArchItem name="ArtifactApprovalQueue" type="Card" desc="Pending artifact approvals" />
              </div>
            </div>

            {/* Commander Validation Components */}
            <div>
              <h3 className="text-lg font-semibold text-pink-400 mb-4">Commander Validation</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <ArchItem name="ActivityPrinciplesValidation" type="Dialog" desc="Validate activity principles adherence" />
                <ArchItem name="ProgressReportPrinciplesValidation" type="Dialog" desc="Validate progress report principles" />
                <ArchItem name="DailyTaskPrinciplesValidation" type="Dialog" desc="Validate daily task principles" />
              </div>
            </div>

            {/* Gamification & Skills */}
            <div>
              <h3 className="text-lg font-semibold text-yellow-400 mb-4">Gamification & Skills</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <ArchItem name="GamificationStatsCard" type="Card" desc="XP/SP statistics display" />
                <ArchItem name="SkillsManager" type="Form" desc="Add/edit officer skills" />
                <ArchItem name="SkillProgressionDisplay" type="Display" desc="Skill leveling visualization" />
                <ArchItem name="TrainingRequestDialog" type="Dialog" desc="Request skill training" />
                <ArchItem name="TrainingManagement" type="Section" desc="Manage training requests" />
              </div>
            </div>

            {/* Document Management */}
            <div>
              <h3 className="text-lg font-semibold text-green-400 mb-4">Document Management</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <ArchItem name="FileUploadZone" type="Form" desc="Google Drive file upload interface" />
                <ArchItem name="DocumentTable" type="Table" desc="Document list with actions" />
                <ArchItem name="ContextualDocuments" type="Section" desc="Context-specific document viewer" />
                <ArchItem name="FileRepositoryCard" type="Card" desc="File repository management" />
              </div>
            </div>

            {/* Civilization Map Components */}
            <div>
              <h3 className="text-lg font-semibold text-orange-400 mb-4">Civilization Map (Hex View)</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <ArchItem name="CivMap" type="Complex" desc="Main hex grid visualization" />
                <ArchItem name="CivCanvas" type="Canvas" desc="Canvas-based rendering layer" />
                <ArchItem name="CivSidebar" type="Sidebar" desc="Left sidebar navigation" />
                <ArchItem name="CivRightSidebar" type="Sidebar" desc="Right sidebar with node details" />
                <ArchItem name="CivTopBar" type="TopBar" desc="Top navigation and controls" />
                <ArchItem name="HexGrid" type="Utility" desc="Hex grid calculation utilities" />
                <ArchItem name="NodeSelector" type="Dropdown" desc="Select nodes by level" />
                <ArchItem name="ActivityRTSOverlay" type="Overlay" desc="RTS-style activity control panel" />
                <ArchItem name="ReportActivityIssueModal" type="Dialog" desc="Report issues from map view" />
                <ArchItem name="TaskQuickAdd" type="Form" desc="Quick task creation from map" />
                <ArchItem name="CivilizationHUD" type="HUD" desc="Heads-up display overlay" />
                <ArchItem name="OfficerTaskInspector" type="Panel" desc="Inspect officer's tasks" />
                <ArchItem name="ProgressReportModal" type="Dialog" desc="Submit progress reports" />
                <ArchItem name="TaskBlockerDialog" type="Dialog" desc="Report task blockers" />
                <ArchItem name="TaskDiagnosticsPanel" type="Panel" desc="Task health diagnostics" />
                <ArchItem name="ActivityDailyTasksSection" type="Section" desc="Activity's daily tasks list" />
                <ArchItem name="ActivityIssuesSection" type="Section" desc="Activity's issues list" />
                <ArchItem name="ActivityTaskLogView" type="View" desc="Task history for activity" />
                <ArchItem name="KRProgressChart" type="Chart" desc="Key results progress visualization" />
                <ArchItem name="TaskHistoryTimeline" type="Timeline" desc="Timeline of task changes" />
                <ArchItem name="OfficerUserManualDialog" type="Dialog" desc="View officer user manual" />
              </div>
            </div>

            {/* Profile & Officer Components */}
            <div>
              <h3 className="text-lg font-semibold text-slate-400 mb-4">Profile & Officer Management</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <ArchItem name="UserManualSection" type="Section" desc="Officer's user manual editor" />
                <ArchItem name="NotificationSettingsSection" type="Section" desc="Notification preferences" />
                <ArchItem name="GameGuideDialog" type="Dialog" desc="Game mechanics tutorial" />
              </div>
            </div>

            {/* Bot & AI Components */}
            <div>
              <h3 className="text-lg font-semibold text-violet-400 mb-4">Bot & AI Integration</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <ArchItem name="BotClarificationChatModal" type="Dialog" desc="Chat with AI officer for task clarification" />
                <ArchItem name="SqlChatAssistant" type="Chat" desc="HexDB SQL assistant" />
              </div>
            </div>

            {/* Common/Utility Components */}
            <div>
              <h3 className="text-lg font-semibold text-slate-400 mb-4">Common Utilities</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <ArchItem name="SearchableTagSelect" type="Input" desc="Searchable dropdown with typeahead" />
                <ArchItem name="ConfirmableDialog" type="Wrapper" desc="Dialog with unsaved changes warning" />
                <ArchItem name="ArchiveWarningDialog" type="Dialog" desc="Warn about archiving with dependents" />
                <ArchItem name="ArchiveConfirmDialog" type="Dialog" desc="Simple archive confirmation" />
                <ArchItem name="ArchivedParentBadge" type="Badge" desc="Indicate archived parent entity" />
                <ArchItem name="AutoScrollContainer" type="Wrapper" desc="Auto-scroll to bottom container" />
                <ArchItem name="PullToRefresh" type="Wrapper" desc="Mobile pull-to-refresh wrapper" />
              </div>
            </div>

            {/* Notification & Communication */}
            <div>
              <h3 className="text-lg font-semibold text-blue-400 mb-4">Notifications & Communication</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <ArchItem name="NotificationCenter" type="Panel" desc="Notification bell with list" />
                <ArchItem name="OfficerNotificationBell" type="Component" desc="Per-officer notification indicator" />
                <ArchItem name="FeedbackButton" type="Button" desc="Submit feedback from any page" />
              </div>
            </div>

            {/* Lighthouse Integration */}
            <div>
              <h3 className="text-lg font-semibold text-amber-300 mb-4">Lighthouse Integration</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <ArchItem name="LighthouseLoginModal" type="Dialog" desc="Lighthouse external link warning" />
              </div>
            </div>

            {/* RBAC & Access Control */}
            <div>
              <h3 className="text-lg font-semibold text-red-400 mb-4">RBAC & Access Control</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <ArchItem name="RolePermissionsEditor" type="Editor" desc="Edit role-based permissions (Commander in Chief only)" />
                <ArchItem name="UserNotRegisteredError" type="Error" desc="Display when user lacks officer profile" />
              </div>
            </div>

            {/* Layout & Navigation */}
            <div>
              <h3 className="text-lg font-semibold text-indigo-300 mb-4">Layout & Navigation</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <ArchItem name="Layout" type="Layout" desc="App layout with sidebar navigation" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>);

}

function ArchItem({ name, type, desc }) {
  return (
    <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-800/50 hover:border-slate-700 transition-colors">
      <div className="flex items-start justify-between gap-2 mb-1">
        <span className="text-slate-200 font-medium text-sm">{name}</span>
        <Badge variant="outline" className="text-[10px] text-slate-500 border-slate-700 shrink-0">{type}</Badge>
      </div>
      <p className="text-xs text-slate-500 leading-tight">{desc}</p>
    </div>
  );
}

function ArchitectureBox({ icon: Icon, title, color, description, isSelected, onClick, isHighlighted }) {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className="cursor-pointer"
      onClick={onClick}
      animate={isHighlighted ? { scale: [1, 1.05, 1] } : {}}
      transition={isHighlighted ? { repeat: Infinity, duration: 2 } : {}}>

      <Card className={`glass-panel border-none overflow-hidden transition-all duration-300 ${
      isSelected ? 'ring-2 ring-white/50 shadow-xl' : ''} ${
      isHighlighted ? 'ring-4 ring-cyan-400/80 shadow-2xl shadow-cyan-500/50' : ''}`}>
        <div className={`h-2 bg-gradient-to-r ${color}`} />
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-lg bg-gradient-to-br ${color} shadow-lg`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-slate-600 mb-2 text-lg font-semibold">{title}</h3>
              <motion.p className="text-slate-500 text-sm"

              initial={{ height: isSelected ? 0 : 'auto', opacity: isSelected ? 0 : 1 }}
              animate={{ height: isSelected ? 'auto' : 'auto', opacity: 1 }}
              transition={{ duration: 0.3 }}>

                {description}
              </motion.p>
            </div>
          </div>
          {isSelected &&
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 pt-4 border-t border-slate-700">

              <Badge className="bg-white/10 text-white border-none">
                Click again to collapse
              </Badge>
            </motion.div>
          }
        </CardContent>
      </Card>
    </motion.div>);

}

function FlowStep({ number, title, description, color }) {
  return (
    <div className="flex items-start gap-4">
      <div className={`flex-shrink-0 w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center ${color} font-bold`}>
        {number}
      </div>
      <div className="flex-1">
        <h4 className="text-slate-600 mb-1 font-semibold">{title}</h4>
        <p className="text-slate-500 text-sm">{description}</p>
      </div>
      <ArrowRight className="flex-shrink-0 w-5 h-5 text-slate-600 mt-1" />
    </div>);

}